import "pages/StandardTreatment/shared/masterData.scss";
import React, { useEffect, useMemo, useState } from "react";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { toast } from "react-toastify";
import ActionButton from "components/atoms/ActionButton";
import InputField from "components/atoms/InputField";
import TableDataComponent from "components/organisms/TableData";
import useServiceData from "pages/StandardTreatment/shared/useServiceData";
import useBootstrapModal from "pages/StandardTreatment/shared/useBootstrapModal";
import { ErrorState } from "pages/StandardTreatment/shared/ui";
import Wilayah from "services/Wilayah";
import EditRegionModal, { REGION_MODAL_ID } from "./EditRegionModal";

const wilayahService = new Wilayah();

// Menu level Nasional: setiap region menampung daftar provinsinya
// (dipakai resolusi Lab Tingkat 4 saat pengiriman sampel).
const RegionalProvinsi = () => {
  const [keyword, setKeyword] = useState("");
  const { modal, open, close } = useBootstrapModal(REGION_MODAL_ID);

  useEffect(() => {
    document.title = "SISMAL | Regional";
  }, []);

  const { data, loading, error, reload } = useServiceData(
    () =>
      Promise.all([
        wilayahService.getRegions(),
        wilayahService.getAll("province", { page: 1, limit: 9999 }),
      ]).then(([regionData, provinceData]) => ({
        regions: regionData?.regions || [],
        provinces: provinceData?.province || [],
      })),
    []
  );

  const regions = data?.regions || [];
  const provinces = data?.provinces || [];

  const rows = useMemo(() => {
    const all = data?.regions || [];
    const q = keyword.trim().toLowerCase();
    if (!q) return all;
    return all.filter(
      (r) =>
        String(r.name).toLowerCase().includes(q) ||
        String(r.id).includes(q) ||
        (r.provinces || []).some((p) =>
          String(p.name).toLowerCase().includes(q)
        )
    );
  }, [data, keyword]);

  const columns = useMemo(
    () => [
      { header: "Kode", accessorKey: "id", size: 80 },
      { header: "Region", accessorKey: "name", size: 200 },
      {
        header: "Provinsi",
        size: 400,
        cell: ({ row }) => {
          const list = row.original.provinces || [];
          if (list.length === 0) {
            return <span className="mps-pill is-warning">Belum ada provinsi</span>;
          }
          return (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {list.map((p) => (
                <span key={p.id} className="mps-pill is-success">
                  {p.name}
                </span>
              ))}
            </div>
          );
        },
      },
      {
        header: "Aksi",
        size: 140,
        cell: ({ row }) => (
          <div className="row-actions">
            <ActionButton btnType="edit" onClick={() => open("edit", row.original)} />
          </div>
        ),
      },
    ],
    []
  );

  const tableInstance = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const saveRegion = (provinceIds) =>
    wilayahService
      .updateRegionProvinces(modal.item.id, provinceIds)
      .then(() => {
        close();
        toast.success(`Provinsi Region ${modal.item.name} berhasil diperbarui`);
        reload();
      });

  return (
    <div className="master-data-page">
      <h1 className="page-title">Regional</h1>
      <p className="page-subtitle">
        Pemetaan provinsi ke region. Region dipakai untuk menentukan Balai
        Laboratorium Kesehatan Masyarakat (Lab Tingkat 4) saat pengiriman sampel.
      </p>

      <div className="action-wrapper">
        <div className="action-search">
          <InputField
            placeholder="Cari region atau provinsi..."
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
          />
        </div>
      </div>

      {error ? (
        <div className="mt-4">
          <ErrorState messages={error} onRetry={reload} />
        </div>
      ) : (
        <TableDataComponent
          columnLength={columns.length}
          tableInstance={tableInstance}
          onPageChange={() => {}}
          loading={loading}
          currentPage={1}
          limit={rows.length || 1}
          total={rows.length}
        />
      )}

      <EditRegionModal
        key={modal.openCount}
        region={modal.item}
        provinces={provinces}
        regions={regions}
        onSubmit={saveRegion}
      />
    </div>
  );
};

export default RegionalProvinsi;
